import styled from '@emotion/styled';
import type { SymbolTypes } from '@racemap/utilities/consts/map';
import { Button, Divider, Flex, Select, Space } from 'antd';
import type { ReactNode } from 'react';
import { toast } from 'react-toastify';
import { useImageUpload } from '../../../lib/customHooks';
import { IconPlus } from '../../Icon';
import { IconInfoMapping } from './IconInfoMapping';

type Props = {
  value: string | null;
  onChange: (arg0: string | null) => void;
};
export const ICON_CDN_PATH = 'https://static-cdn.racemap.com/img/map-icons';

export function IconChooser({ onChange, value }: Props) {
  const { uploadImage, state } = useImageUpload({ imageClass: 'poiIcon' });
  const options = Object.keys(IconInfoMapping).map((value) => ({
    value: `${ICON_CDN_PATH}/${value}_borderless.svg`,
    label: IconInfoMapping[value as SymbolTypes].label,
  }));
  const maxLabelLength = Math.max(...options.map((option) => option.label.length));

  const handleUploadFile = async () => {
    try {
      const { uris, error } = await uploadImage();
      if (error) throw error;
      if (uris?.icon == null) throw new Error('Failed to upload image');

      onChange(uris.icon);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image', error);
      toast.error('Error uploading image!');
    }
  };

  return (
    <Container>
      <Select
        value={value}
        onSelect={(newValue) => onChange(newValue)}
        options={options}
        showSearch
        optionFilterProp="label"
        filterSort={(optionA, optionB) =>
          (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
        }
        placeholder="Select an Icon"
        loading={state.status === 'loading'}
        allowClear
        optionRender={(option) => <Label label={option.label} value={option.value} />}
        onClear={() => onChange(null)}
        labelRender={(option) => <Label label={option.label} value={option.value} />}
        dropdownStyle={{ width: `calc(${maxLabelLength}ch + 40px)` }}
        dropdownRender={(menu) => (
          <div>
            {menu}
            <Divider style={{ margin: '8px 0' }} />
            <Flex justify="center" align="center" style={{ padding: '0 8px 4px' }}>
              <Button ghost onClick={handleUploadFile} icon={<IconPlus />} type="link">
                Add Custom Icon
              </Button>
            </Flex>
          </div>
        )}
      />
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  .ant-select {  
    width: 100%;

    .ant-select-selector {
      border: none;
    }
  }
`;

const Label = (option: { label: ReactNode; value: number | string | undefined }) => {
  if (option.value === undefined) return null;
  const value = option.value.toString();

  return (
    <Space>
      <img src={value} height="32" width="32" alt={option.label?.toString()} />
      <span>{option.label || 'Custom'}</span>
    </Space>
  );
};
